/**
 * initLiquidGlass
 *
 * Decides whether to enable the SVG-displacement refraction on our frosted
 * glass surfaces. We do NOT rely on `@supports (backdrop-filter: url(...))`
 * alone: that query returns true in some browsers that then fail to render
 * the filter (notably Firefox, which can also drop the whole declaration).
 *
 * So we combine a syntax check with an engine check: url() inside
 * backdrop-filter is, as of 2026, only rendered correctly by Chromium. On
 * Chromium we add `.bp-glass-distort` to <html>, which switches the glass
 * surfaces (nav pill, mega menu) to the enhanced backdrop-filter in
 * liquidGlass.css. Safari / Firefox keep the polished blur + tint + edge
 * lighting fallback.
 */
export function initLiquidGlass() {
  if (typeof document === 'undefined' || typeof navigator === 'undefined') return;

  try {
    const ua = navigator.userAgent || '';
    const isFirefox = /Firefox\//.test(ua);
    // Safari = has "Safari" + "Version/" but is NOT Chrome/Chromium/Android.
    const isSafari =
      /Safari\//.test(ua) && /Version\//.test(ua) && !/Chrom(e|ium)\//.test(ua);

    // Chromium family (Chrome, Edge, Brave, Opera, Samsung...) is where url()
    // backdrop distortion actually paints. Prefer UA-Client-Hints when present.
    const brands = (navigator.userAgentData && navigator.userAgentData.brands) || [];
    const isChromiumByHints = brands.some((b) => /Chromium/i.test(b.brand));
    const isChromiumByUA = /Chrom(e|ium)\//.test(ua) && !isFirefox;
    const isChromium = isChromiumByHints || isChromiumByUA;

    const syntaxOk =
      typeof CSS !== 'undefined' &&
      typeof CSS.supports === 'function' &&
      (CSS.supports('backdrop-filter', 'url("#x")') ||
        CSS.supports('-webkit-backdrop-filter', 'url("#x")'));

    if (syntaxOk && isChromium && !isFirefox && !isSafari) {
      document.documentElement.classList.add('bp-glass-distort');
    }
  } catch {
    /* Any failure -> leave the fallback glass in place. */
  }
}

export default initLiquidGlass;
